/**
 * Kate K9 — near-duplicate service names, in the owner's catalog.
 *
 * Kate's walkthrough (2026-10-12) found "dental brushing" beside
 * "toothbrushing" and two deshedding entries, left from the owner's own
 * edits. The server refuses only an EXACT case-insensitive match (the
 * grooming menu's 409) or a repeated engine key (the top-level 409); it
 * never will refuse a near match, because the owner may legitimately want
 * two ("Bath" and "Bath & Trim" are both real). So the forms warn — "Looks
 * like 'X' already exists; keep both?" — and the owner decides.
 *
 * What counts as "looks like": the same letters and digits once case,
 * spacing, punctuation, "&"/"and", a trailing plural "s" and the two
 * synonyms Kate hit (dental/teeth → tooth) are folded away. Deliberately
 * small: a clever matcher that flags "Bath" against "Bath & Trim" would
 * teach the owner to click through the warning.
 */

/** Per-word folds. Keys and values are already lower-case, letters only. */
const WORD_SYNONYMS: Readonly<Record<string, string>> = {
  dental: 'tooth',
  teeth: 'tooth',
  and: '',
};

function foldWord(word: string): string {
  const mapped = WORD_SYNONYMS[word];
  if (mapped !== undefined) return mapped;
  // "nails" → "nail", "walks" → "walk"; never "ss" words ("grass") and
  // never short tokens ("is", "xs").
  if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

/**
 * The comparison key for a service name. Two names with the same key are
 * near-duplicates. Empty input yields an empty key, which never matches
 * (the forms refuse a blank name before they get here).
 */
export function normalizeServiceName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // accents: "Café" → "Cafe"
    .toLowerCase()
    .replace(/&/g, ' and ')
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 0)
    .map(foldWord)
    .join('');
}

/**
 * The first existing row whose name folds to the candidate's — or null.
 * `excludeId` is the row being renamed, so a name compared with itself is
 * never its own duplicate.
 */
export function findNearDuplicateName<T extends { id: string; displayName: string }>(
  candidate: string,
  existing: ReadonlyArray<T>,
  excludeId?: string,
): T | null {
  const key = normalizeServiceName(candidate);
  if (key.length === 0) return null;
  return existing.find((row) => row.id !== excludeId && normalizeServiceName(row.displayName) === key) ?? null;
}

/** The soft question both admin forms ask, word for word (Kate K9). */
export function nearDuplicateQuestion(existing: string): string {
  return `Looks like '${existing}' already exists; keep both?`;
}
